import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  StackLayout,
  HeadingText,
  ParagraphText,
  useMediaQuery,
  queryHelpers,
  ImageMessage,
} from '@leagueplatform/genesis-core';
import { captureAppMessage } from '@leagueplatform/observability';
import { useGetUserProfile } from '@leagueplatform/web-common';
import { FullPageError } from 'components/error/full-page-error.component';
import { BaseModalContainer } from 'components/modals/common/base-modal.component';
import { useDeleteAccount } from 'hooks/use-delete-account';
import { ErrorTypes } from 'hooks/use-error-message.hook';
import { getPartnerIdentityToken } from 'api/get-partner-id-token';
import { PARTNER_CONFIG } from 'common/partner-config';
import { SENTRY_CONSTANTS } from 'common/constants';
import { FullPageLoadingSpinner } from 'components/loaders/full-page-loading-spinner.component';
import exitWarning from 'assets/exit-warning.svg';
import { ModalActionButton } from './common/modal-action-button.component';

type DeleteAccountModalProps = {
  onDeleted: () => void;
};

export const DeleteAccountModal = ({ onDeleted }: DeleteAccountModalProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));
  const deleteAccountMutation = useDeleteAccount({
    onDeleted,
  });
  const userProfileQuery = useGetUserProfile();

  if (userProfileQuery.isLoading) {
    return <FullPageLoadingSpinner />;
  }

  if (!userProfileQuery.isSuccess || deleteAccountMutation.isError) {
    return <FullPageError errorType={ErrorTypes.API_ERROR} />;
  }
  const { userId } = userProfileQuery.data;

  const handleDeleteAccount = async () => {
    try {
      const partnerTokenResponse = await getPartnerIdentityToken(
        PARTNER_CONFIG.PARTNER_ID,
      );
      const partnerToken = partnerTokenResponse.data.attributes.idToken;
      if (!partnerToken) {
        throw new Error('Failed to fetch partner token before deletion');
      }
      localStorage.setItem('tempPartnerToken', partnerToken);
      deleteAccountMutation.mutate(userId);
    } catch (error: any) {
      captureAppMessage('Account Deletion Error', {
        context: {
          contextDetails: {
            info: `Failed to delete account. Error: ${error.message}`,
          },
        },
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
        severityLevel: 'error',
      });
    }
  };

  return (
    <BaseModalContainer
      title={formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' })}
      footer={
        <StackLayout
          horizontalAlignment="end"
          orientation={{
            '@initial': 'horizontal',
            '@mobile': 'vertical',
          }}
          spacing="$one"
          css={{ flexGrow: 0 }}
        >
          <ModalActionButton
            actionType="cancel"
            labelId="STR_NEVER_MIND"
            isMobile={isMobile}
            cssStyle={{
              color: '$secondaryTextDefault',
              '&:hover, &:active, &:focus': {
                backgroundColor: '$interactiveBackgroundHovered',
                color: '$interactiveActionHovered',
                textDecoration: 'none',
              },
            }}
          />
          <ModalActionButton
            actionType="submit"
            onClick={handleDeleteAccount}
            labelId="STR_YES_DELETE_ACCOUNT"
            isMobile={isMobile}
            isDestructive
          />
        </StackLayout>
      }
    >
      <ImageMessage
        image={exitWarning}
        css={{
          flexGrow: 1,
          padding: '$two $half $five $half',
          textAlign: 'left',
          '@mobile': { textAlign: 'center' },
        }}
      >
        <HeadingText
          level="2"
          size={{
            '@initial': 'lg',
            '@mobile': 'xl',
          }}
          css={{ paddingBlock: '$one', fontWeight: 'bold' }}
        >
          {formatMessage({ id: 'STR_ARE_YOU_SURE' })}
        </HeadingText>
        <ParagraphText>
          {formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_1' })}
        </ParagraphText>
        <ParagraphText css={{ marginBlock: '$one' }}>
          {formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_2' })}
        </ParagraphText>
      </ImageMessage>
    </BaseModalContainer>
  );
};
